import {
  informationFieldConfigValueSchema,
  usePatientInformationFields,
  usePatients,
} from "@/api/Patients";
import { usePrimaryResponsibleEmployees } from "@/api/Employee";
import {
  formatPersonalIdentityNumber,
  getPatientFullName,
  patientStatusSchema,
} from "@medoma/models/patients";
import { employeeName } from "@medoma/models/shifts";
import { Loading } from "@medoma/components/Loading/Loading";
import { Trans, useLingui } from "@lingui/react/macro";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";
import styles from "./PatientsList.module.scss";
import { Text, TextWithLineBreaks } from "@medoma/components/Text/Text";
import { NavLink, useLocation } from "react-router";
import NoResults from "@/components/NoResults/NoResults";
import { filterByNameTerms } from "@medoma/components/SearchField/searchUtils";
import { usePatientNameFilter } from "./DailyOverviewContext";
import { useCallback, useMemo, type CSSProperties } from "react";
import { humanFormat } from "@medoma/models/date-and-time";
import EmployeeName from "@/components/EmployeeName/EmployeeName";
import { useSortState } from "@/components/SortableColumnHeader/useSortState";
import { SortableColumnHeader } from "@/components/SortableColumnHeader/SortableColumnHeader";
import { useSortedItems } from "@/components/SortableColumnHeader/useSortedItems";

type AdmittedSortColumn =
  | "name"
  | "area"
  | "primaryResponsible"
  | "admittedAt"
  | "plannedDischarge";

const AdmittedPatientsList = () => {
  const { t } = useLingui();
  const { search } = useLocation();
  const {
    data: patients,
    isPending,
    isError,
    error,
  } = usePatients({
    statuses: [patientStatusSchema.enum.admitted],
  });
  const {
    data: fieldsConfig,
    isPending: isPendingFieldsConfig,
    isError: isErrorFieldsConfig,
    error: fieldsConfigError,
  } = usePatientInformationFields();
  const { employeesMap } = usePrimaryResponsibleEmployees();
  const { value: patientNameFilter } = usePatientNameFilter();
  const { sortState, toggleSort } = useSortState<AdmittedSortColumn>({
    column: "name",
    direction: "asc",
  });

  const unpausedPatients = useMemo(
    () =>
      (patients ?? []).filter(
        (patient) =>
          patient.status === patientStatusSchema.enum.admitted &&
          !patient.pausedAt,
      ),
    [patients],
  );
  const filteredPatients = useMemo(
    () =>
      unpausedPatients.filter((patient) =>
        filterByNameTerms(getPatientFullName(patient.name), patientNameFilter),
      ),
    [unpausedPatients, patientNameFilter],
  );
  type AdmittedPatient = (typeof filteredPatients)[number];
  const comparePatients = useCallback(
    (a: AdmittedPatient, b: AdmittedPatient, column: AdmittedSortColumn) => {
      switch (column) {
        case "name":
          return getPatientFullName(a.name).localeCompare(
            getPatientFullName(b.name),
          );
        case "area":
          return (a.area?.displayName ?? "").localeCompare(
            b.area?.displayName ?? "",
          );
        case "primaryResponsible": {
          const employeeA = a.primaryResponsible
            ? employeesMap.get(a.primaryResponsible)
            : undefined;
          const employeeB = b.primaryResponsible
            ? employeesMap.get(b.primaryResponsible)
            : undefined;
          const nameA = employeeA
            ? employeeName(employeeA, { employeeNameFormat: "full" })
            : "";
          const nameB = employeeB
            ? employeeName(employeeB, { employeeNameFormat: "full" })
            : "";
          if (nameA === nameB) {
            return 0;
          }
          if (nameA === "") {
            return 1;
          }
          if (nameB === "") {
            return -1;
          }
          return nameA.localeCompare(nameB);
        }
        case "admittedAt": {
          const timeA =
            a.status === patientStatusSchema.enum.admitted
              ? a.admittedAt.getTime()
              : 0;
          const timeB =
            b.status === patientStatusSchema.enum.admitted
              ? b.admittedAt.getTime()
              : 0;
          return timeA - timeB;
        }
        case "plannedDischarge": {
          const dateA = a.plannedDischargeDateTime;
          const dateB = b.plannedDischargeDateTime;
          if (!dateA && !dateB) {
            return 0;
          }
          if (!dateA) {
            return 1;
          }
          if (!dateB) {
            return -1;
          }
          return dateA.getTime() - dateB.getTime();
        }
      }
    },
    [employeesMap],
  );
  const visiblePatients = useSortedItems(
    filteredPatients,
    sortState,
    comparePatients,
  );

  if (isPending || isPendingFieldsConfig) {
    return <Loading message={t`Hämtar patienter`} padding={24} />;
  }

  if (isError || isErrorFieldsConfig) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta patienter.`} ${deducedError(error ?? fieldsConfigError)}`}
        padding={24}
      />
    );
  }

  if (unpausedPatients.length === 0) {
    return <NoResults message={t`Inga patienter hittades`} padding={24} />;
  }

  if (visiblePatients.length === 0) {
    return (
      <NoResults message={t`Inga patienter matchar filtret`} padding={24} />
    );
  }

  const hiddenValue = informationFieldConfigValueSchema.enum.hidden;
  const showPrimaryResponsible =
    fieldsConfig.PrimaryResponsible !== hiddenValue;
  const showReasonForAdmission =
    fieldsConfig.ReasonForAdmission !== hiddenValue;
  const showCareSummary = fieldsConfig.CareSummary !== hiddenValue;
  const showPlannedDischarge =
    fieldsConfig.PlannedDischargeDateTime !== hiddenValue;

  const columnCount =
    3 + // name + area + admittedAt
    (showPrimaryResponsible ? 1 : 0) +
    (showReasonForAdmission ? 1 : 0) +
    (showCareSummary ? 1 : 0) +
    (showPlannedDischarge ? 1 : 0);

  return (
    <nav aria-label={t`Patientlista`} className={styles.patientsListNav}>
      <ul
        className={styles.patientsList}
        style={{ "--column-count": columnCount } as CSSProperties}
      >
        <li className={styles.patientsListHeader}>
          <SortableColumnHeader
            columnId="name"
            sortState={sortState}
            toggleSort={toggleSort}
          >
            <Trans>Namn</Trans>
          </SortableColumnHeader>
          <SortableColumnHeader
            columnId="area"
            sortState={sortState}
            toggleSort={toggleSort}
          >
            <Trans>Område</Trans>
          </SortableColumnHeader>
          {showPrimaryResponsible ? (
            <SortableColumnHeader
              columnId="primaryResponsible"
              sortState={sortState}
              toggleSort={toggleSort}
            >
              <Trans>Patientansvarig</Trans>
            </SortableColumnHeader>
          ) : null}
          {showReasonForAdmission ? (
            <Text element="span" weight="medium" size="small">
              <Trans>Inskrivningsorsak</Trans>
            </Text>
          ) : null}
          {showCareSummary ? (
            <Text element="span" weight="medium" size="small">
              <Trans>Vårdsammanfattning</Trans>
            </Text>
          ) : null}
          <SortableColumnHeader
            columnId="admittedAt"
            sortState={sortState}
            toggleSort={toggleSort}
          >
            <Trans>Inskriven</Trans>
          </SortableColumnHeader>
          {showPlannedDischarge ? (
            <SortableColumnHeader
              columnId="plannedDischarge"
              sortState={sortState}
              toggleSort={toggleSort}
            >
              <Trans>Planerad utskrivning</Trans>
            </SortableColumnHeader>
          ) : null}
        </li>
        {visiblePatients.map((patient) => (
          <li key={patient.id}>
            <NavLink
              to={{ pathname: patient.id, search }}
              className={styles.patient}
            >
              <span className={styles.nameAndPIN}>
                <Text element="span" weight="medium">
                  {getPatientFullName(patient.name)}
                </Text>
                <Text element="span" size="small" color="faded">
                  {formatPersonalIdentityNumber({
                    personalIdentityNumber: patient.personalIdentityNumber,
                    personalIdentityNumberType:
                      patient.personalIdentityNumberType,
                  })}
                </Text>
              </span>
              <Text element="span">{patient.area?.displayName ?? "–"}</Text>
              {showPrimaryResponsible ? (
                <span>
                  {patient.primaryResponsible ? (
                    <EmployeeName
                      employeeId={patient.primaryResponsible}
                      showCompetence={false}
                    />
                  ) : (
                    <Text element="span">–</Text>
                  )}
                </span>
              ) : null}
              {showReasonForAdmission ? (
                patient.reasonForAdmission ? (
                  <TextWithLineBreaks>
                    {patient.reasonForAdmission}
                  </TextWithLineBreaks>
                ) : (
                  <Text element="span">–</Text>
                )
              ) : null}
              {showCareSummary ? (
                patient.careSummary ? (
                  <TextWithLineBreaks>{patient.careSummary}</TextWithLineBreaks>
                ) : (
                  <Text element="span">–</Text>
                )
              ) : null}
              <Text element="span">
                {patient.status === patientStatusSchema.enum.admitted
                  ? humanFormat(patient.admittedAt, { dateStyle: "short" })
                  : "–"}
              </Text>
              {showPlannedDischarge ? (
                <Text element="span">
                  {patient.plannedDischargeDateTime
                    ? humanFormat(patient.plannedDischargeDateTime, {
                        dateStyle: "short",
                      })
                    : "–"}
                </Text>
              ) : null}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export { AdmittedPatientsList };
